import { registerLocaleData } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import localeEN from "@angular/common/locales/en";
import localeNL from "@angular/common/locales/nl";
import {
  APP_INITIALIZER,
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import {
  MAT_DATE_FORMATS,
  MatNativeDateModule,
  MatRippleModule,
} from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MAT_DIALOG_DEFAULT_OPTIONS } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { provideMomentDateAdapter } from "@angular/material-moment-adapter";
import { bootstrapApplication } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter } from "@angular/router";

import {
  ApiModule as AuthApiModule,
  Configuration as AuthApiConfiguration,
} from "@app/api/auth";
import { AppComponent } from "@app/app.component";
import { routes as AppRoutes } from "@app/app-routes";
import { ELocale } from "@core/enums/elocale.enum";
import { Language } from "@core/enums/language";
import { GlobalErrorHandler } from "@core/handlers/global-error.handler";
import { RegisterInterceptor } from "@core/interceptors/register.interceptor";
import { AccountsEffects } from "@features/accounts/store/accounts.effects";
import { AuthEffects } from "@features/auth/store/auth.effects";
import { ClientEffects } from "@features/client/store/client.effects";
import { CountryEffects } from "@features/country/store/country.effects";
import { IntermediaryEffects } from "@features/intermediary/store/intermediary.effects";
import { MessagesEffects } from "@features/messages/store/messages.effect";
import { provideEffects } from "@ngrx/effects";
import { provideStore } from "@ngrx/store";
import {
  provideTranslateService,
  TranslateLoader,
  TranslateService,
} from "@ngx-translate/core";
import { AuthService } from "@services/auth.service";
import * as fromApp from "@store/app.reducer";
import {
  NGX_SKELETON_LOADER_CONFIG,
  NgxSkeletonLoaderConfig,
  NgxSkeletonLoaderModule,
} from "ngx-skeleton-loader";
import { MultiTranslateHttpLoader } from "ngx-translate-multi-http-loader";

import { environment } from "./environments/environment";

export function authApiConfigurationFactory(): AuthApiConfiguration {
  return new AuthApiConfiguration({
    basePath: environment.AUTH_API_BASE_URL,
    // Send/receive the HttpOnly refresh-token cookie on auth requests.
    withCredentials: true,
  });
}

const skeletonConfig: NgxSkeletonLoaderConfig = {
  theme: {
    extendsFromRoot: true,
    height: "5vh",
    margin: 0,
    padding: 0,
  },
  appearance: "",
  animation: false,
  loadingText: "",
  count: 0,
  ariaLabel: "",
};

if (environment.production) {
  enableProdMode();
}
registerLocaleData(localeNL, "nl-NL");
registerLocaleData(localeEN, "en-GB");

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    { provide: LOCALE_ID, useValue: "nl-NL" },

    provideRouter(AppRoutes),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(AuthApiModule.forRoot(authApiConfigurationFactory)),
    provideTranslateService({
      defaultLanguage: "nl",
      useDefaultLang: true,
      loader: {
        provide: TranslateLoader,
        useFactory: (handler: HttpBackend) =>
          new MultiTranslateHttpLoader(handler, [
            { prefix: "./assets/i18n/auth/", suffix: ".json" },
            { prefix: "./assets/i18n/common/", suffix: ".json" },
            { prefix: "./assets/i18n/not-found/", suffix: ".json" },
            { prefix: "./assets/i18n/shared/", suffix: ".json" },
            { prefix: "./assets/i18n/client/", suffix: ".json" },
          ]),
        deps: [HttpBackend],
      },
    }),
    // Honour the language picked on the login screen (or in the sidenav) so a
    // reload does not fall back to the default language.
    provideAppInitializer((): void => {
      const translate = inject(TranslateService);
      const storedLanguage = localStorage.getItem(ELocale.localStorageKey);
      if (
        storedLanguage &&
        Object.values<string>(Language).includes(storedLanguage)
      ) {
        translate.use(storedLanguage);
      }
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RegisterInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      // Attempt a silent refresh before routing starts, so a valid session (HttpOnly refresh
      // cookie) survives a full page reload even though the access token lives only in memory.
      provide: APP_INITIALIZER,
      useFactory: (authService: AuthService): (() => Promise<void>) => {
        return (): Promise<void> => authService.bootstrapSession();
      },
      deps: [AuthService],
      multi: true,
    },
    { provide: NGX_SKELETON_LOADER_CONFIG, useValue: skeletonConfig },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: "DD-MM-YYYY",
        },
        display: {
          dateInput: "DD-MM-YYYY",
          monthYearLabel: "MMM YYYY",
          dateA11yLabel: "LL",
          monthYearA11yLabel: "MMMM YYYY",
        },
      },
    },
    provideMomentDateAdapter(),
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        autoFocus: "dialog",
        restoreFocus: true,
        // Every dialog gets the design system's dialog layout: title and
        // actions pinned, the content the only part that scrolls. Without it
        // the surface also stays at Material's 80% opening scale, which is
        // why the group and employee dialogs looked small and cramped.
        panelClass: "talero-dialog",
        maxWidth: "92vw",
        maxHeight: "92vh",
      },
    },
    MatIconModule,
    MatListModule,
    MatCardModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatCardModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule,
    MatExpansionModule,
    MatListModule,
    provideAnimations(),
    MatRippleModule,
    provideStore(fromApp.APP_REDUCER),
    provideEffects(
      AuthEffects,
      IntermediaryEffects,
      ClientEffects,
      CountryEffects,
      AccountsEffects,
      MessagesEffects
    ),
    NgxSkeletonLoaderModule,
  ],
}).catch((err) => console.error(err));
